import React from 'react';
import { CollapseBlock, RawHtml } from '../index.jsx';
import { useTranslation } from 'react-i18next';
import FijInterestForm from '../../pages/CMSPages/FriInternalJobs/FijInterests/FijInterestEdit/FijInterestForm.jsx';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { routes } from '../../../utils/helpers/ApiClient.jsx';
import { useNavigate } from 'react-router-dom';
import { FIJ_JOB_STATUS } from '../../../utils/constants/enums.js';
import FormButton from '../../antd/form/FormButton/FormButton.jsx';
import { Row } from 'antd';
import {
  ROLE_FRI_INTERNAL_JOBS_ADMIN,
  ROLE_FRI_INTERNAL_JOBS_INTERN,
  ROLE_FRI_INTERNAL_JOBS_LECTURER,
} from '../../../utils/constants/roles.js';
import useUserHasRole from '../../../hooks/useUserHasRoles.jsx';
import '../styles/DetailTable.sass';
import '../styles/RichContent.sass';

const FijJobDetail = ({
  internalJob,
  onSendInterestClick,
  interestFormInitialValues,
  fromPublic,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);
  // Derive roles the same way as in FijJobEdit
  const isFriInternalJobsAdmin = useUserHasRole(ROLE_FRI_INTERNAL_JOBS_ADMIN);
  const isFriInternalJobsLecturer = useUserHasRole(
    ROLE_FRI_INTERNAL_JOBS_LECTURER,
  );
  const isFriInternalJobsIntern = useUserHasRole(ROLE_FRI_INTERNAL_JOBS_INTERN);
  const isFriInternalJobOwner = fromPublic
    ? false
    : isFriInternalJobsLecturer &&
      internalJob.teachers.includes(user.id);
  const studentAlreadyHasInterest = fromPublic
    ? false
    : user &&
      isFriInternalJobsIntern &&
      internalJob.studentsInterested.includes(user.id);

  interestFormInitialValues = {
    mail: user?.mail,
    motivation_text: '',
  };

  return (
    <>
      <div className="detail-collapse">
        <CollapseBlock collapsible title={t('Základné informácie')}>
          <table className="table table-striped detail-info-table">
          <tbody>
            <tr>
              <td>
                <b>{t('Názov')}</b>
              </td>
              <td>{internalJob.name}</td>
            </tr>
            <tr>
              <td>
                <b>{t('Lektor')}</b>
              </td>
              <td>{internalJob.supervisor}</td>
            </tr>
            <tr>
              <td>
                <b>{t('Ďalší lektori')}</b>
              </td>
              <td>
                {Array.isArray(internalJob.teacherNames) &&
                internalJob.teacherNames.length > 0
                  ? internalJob.teacherNames.map(function (item, i) {
                      return <p key={i}>{item}</p>;
                    })
                  : '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Realizácia')}</b>
              </td>
              <td>
                {(internalJob.date_from &&
                  new Date(internalJob.date_from).toLocaleDateString()) ||
                  '-'}
                &nbsp;-&nbsp;
                {(internalJob.date_to &&
                  new Date(internalJob.date_to).toLocaleDateString()) ||
                  '-'}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Max. počet študentov')}</b>
              </td>
              <td>{internalJob.student_capacity}</td>
            </tr>
          </tbody>
          </table>
        </CollapseBlock>
        <CollapseBlock collapsible title={t('Náplň FRI praxe')}>
          <div className="detail-rich-section">
            {internalJob.content ? (
              <RawHtml className="rich-content rich-content--lead add-icons-to-file-anchors">
                {internalJob.content}
              </RawHtml>
            ) : (
              '–'
            )}
          </div>
        </CollapseBlock>
        <CollapseBlock collapsible title={t('Očakávaný výsledok')}>
          <div className="detail-rich-section">
            {internalJob.result ? (
              <RawHtml className="rich-content add-icons-to-file-anchors">
                {internalJob.result}
              </RawHtml>
            ) : (
              '–'
            )}
          </div>
        </CollapseBlock>
        <CollapseBlock collapsible title={t('Rozšírené informácie')}>
          <table className="table table-striped detail-info-table">
          <tbody>
            {internalJob.grant ? (
              <tr>
                <td>
                  <b>{t('Výška navrhovaného štipendia')}</b>
                </td>
                <td>{internalJob.grant} €</td>
              </tr>
            ) : null}
            <tr>
              <td>
                <b>{t('Oblasť využitia')}</b>
              </td>
              <td>{internalJob.area_of_use}</td>
            </tr>
          </tbody>
          </table>
        </CollapseBlock>
        <CollapseBlock collapsible title={t('Požiadavky na uchádzača')}>
          <table className="table table-striped detail-info-table">
          <tbody>
            <tr>
              <td>
                <b>{t('Vhodné pre programy')}</b>
              </td>
              <td>
                {internalJob.study_programs.map(function (item, i) {
                  return <p key={i}>{item}</p>;
                })}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('IT zručnosti')}</b>
              </td>
              <td style={{ whiteSpace: 'pre-line' }}>
                {internalJob.it_skills}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Jazykové schopnosti')}</b>
              </td>
              <td style={{ whiteSpace: 'pre-line' }}>
                {internalJob.language_knowledge}
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('Iné požiadavky')}</b>
              </td>
              <td style={{ whiteSpace: 'pre-line' }}>
                {internalJob.other_text}
              </td>
            </tr>
          </tbody>
          </table>
        </CollapseBlock>
      </div>

      {internalJob.status === FIJ_JOB_STATUS.APPROVED &&
        !fromPublic &&
        !isFriInternalJobsLecturer &&
        !studentAlreadyHasInterest && (
          <CollapseBlock collapsible title={t('Prihlásiť sa na FRI prax')}>
            <FijInterestForm
              onSubmit={onSendInterestClick}
              initialValues={interestFormInitialValues}
            />
          </CollapseBlock>
        )}

      {internalJob.status === FIJ_JOB_STATUS.REJECTED &&
        (isFriInternalJobsAdmin ||
          (isFriInternalJobsLecturer && isFriInternalJobOwner)) && (
          <CollapseBlock collapsible title={t('Vaša prax bola zamietnutá')}>
            <p>
              {t(
                'Po osobnom stretnutí s prodekanom pre vzdelávanie je potrebné upraviť fakultnú prax, ktorá po úprave, automaticky prejde do stavu "Čaká na vyjadrenie".',
              )}
            </p>
          </CollapseBlock>
        )}

      {fromPublic && !isFriInternalJobsLecturer && (
        <Row justify="end">
          <FormButton
            onClick={() =>
              navigate(
                `${routes.cmsFijInterestsCreate}/${internalJob.id}/fromList`,
              )
            }
          >
            {t('Mám záujem o FRI prax')}
          </FormButton>
        </Row>
      )}
    </>
  );
};

FijJobDetail.propTypes = {
  internalJob: PropTypes.object.isRequired,
  onSendInterestClick: PropTypes.func.isRequired,
  onApprove: PropTypes.func,
  onRejectClick: PropTypes.func,
  interestFormInitialValues: PropTypes.any,
  refetchInternalJobData: PropTypes.func.isRequired,
  fromPublic: PropTypes.bool,
};

export default FijJobDetail;
